/**
 * Supervisor well helpers. The old circular chip is gone — production paints
 * the 4:3 frame via SupervisorFrame + SupervisorPortrait.
 */
import React from 'react';
import { Image } from 'react-native';
import {
  Avatar,
  AvatarFallbackText,
  Box,
} from '@gluestack-ui/themed';

import {
  OralExamSupervisorStatus,
  type OralExamSupervisorStatus as OralExamSupervisorStatusType,
} from '@/common/api/models/oralExamSupervisorStatus';
import {
  standInImageSource,
  type OralExamLabImageSource,
} from '@/common/oralExam/oralExamLabOverrides';

type SupervisorStatusTranslator = (
  key: string,
  options?: { defaultValue?: string },
) => string;

/** Maps API supervisor status to student-facing strip / well copy. */
export function resolveSupervisorStatusLabel(
  status: OralExamSupervisorStatusType,
  t: SupervisorStatusTranslator,
): string {
  if (status === OralExamSupervisorStatus.here_with_you) {
    return t('oralExam.supervisorStatusHereWithYou', { defaultValue: 'Here with you' });
  }
  if (status === OralExamSupervisorStatus.engaged) {
    return t('oralExam.supervisorStatusEngaged', { defaultValue: 'Engaged' });
  }
  return t('oralExam.supervisorStatusWatching', { defaultValue: 'Watching' });
}

/** Reserved supervision chrome — Watching, Appear, and Engage (see SupervisorFrame). */
export function shouldShowSupervisorWell(status: OralExamSupervisorStatusType): boolean {
  return status === OralExamSupervisorStatus.watching
    || status === OralExamSupervisorStatus.here_with_you
    || status === OralExamSupervisorStatus.engaged;
}

/** Live face layer: Appear and Engage only. Watching stays the pale still. */
export function isSupervisorWellLive(status: OralExamSupervisorStatusType): boolean {
  return status === OralExamSupervisorStatus.here_with_you
    || status === OralExamSupervisorStatus.engaged;
}

/**
 * Photo XOR initials. A URL paints a square cover crop with no radius and no
 * fallback text — Gluestack Avatar mounts initials under the image and they
 * bleed through. Missing URL is initials only.
 */
const SQUARE_PHOTO_STYLE = {
  width: '100%' as const,
  height: '100%' as const,
  borderRadius: 0,
};

export function SupervisorPortrait({
  name,
  photoUrl,
  size = 32,
  fill = false,
}: {
  name: string;
  photoUrl?: OralExamLabImageSource | null;
  size?: number;
  fill?: boolean;
}) {
  const fillBox = fill
    ? {
      position: 'absolute' as const,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    }
    : { w: size, h: size };

  if (photoUrl) {
    return (
      <Box
        testID="oral-exam-supervisor-photo"
        {...fillBox}
        overflow="hidden"
        borderRadius="$none"
      >
        <Image
          source={standInImageSource(photoUrl)}
          accessibilityLabel={name}
          resizeMode="cover"
          style={SQUARE_PHOTO_STYLE}
        />
      </Box>
    );
  }

  return (
    <Box
      testID="oral-exam-supervisor-initials"
      {...fillBox}
      justifyContent="center"
      alignItems="center"
    >
      <Avatar bg="$bgBrandSubtle" size={fill ? 'md' : 'sm'} w={fill ? undefined : size} h={fill ? undefined : size}>
        <AvatarFallbackText color="$textOnBrandSubtle">{name}</AvatarFallbackText>
      </Avatar>
    </Box>
  );
}
